import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import arabesqueGameImg from "../assets/arabesquegame.svg";
import starsAndLightAnimationData from "../assets/starsandlight.json";
import { API_URL } from "../config/api";
import {
  GAME_LEVELS,
  GAME_PLAY_MODES,
  GAME_QUESTION_COUNT,
  isGameTestPlayMode,
} from "../constants/gameLevels";
import { buildGemCatalog, GEM_FILES, isPreciousGem } from "../constants/gems";
import {
  QUESTION_TIME_EXTENDED_LIMIT_SECONDS,
  QUESTION_TIME_LIMIT_SECONDS,
  SERUM_CADRAN_TIME_BONUS_SECONDS,
  getGameAnswerPotionAid,
  GAME_TEST_NAV_POTION_IDS,
} from "../constants/potions";
import { maskDefinition } from "../utils/maskDefinition";
import { typedAnswersMatch } from "../utils/frenchOrthography";
import { filterTypedWord, WORD_MOT_MAX_LENGTH } from "../utils/wordSanitize";
import { dispatchNotificationsChanged } from "../utils/levelHistoryApi";
import { buildPotionHint, shouldAutoHidePotionHint } from "../utils/potionHints";
import {
  readKeyUsagePreference,
  writeKeyUsagePreference,
} from "../utils/keyUsagePreference";
import { shouldProposeMemoryGame } from "../utils/memoryGamePreference";
import { getPotionIdForShortcutEvent } from "../utils/gamePotionShortcutsPreference";
import { MIN_KEY_LEVEL } from "../constants/keys";
import { useGameSessionNav } from "../contexts/GameSessionContext";
import ChestOpenAnimation from "./ChestOpenAnimation";
import { CLOVER_POTION_IDS } from "../constants/cloverPotions";
import CountDownTimer from "./CountDownTimer";
import GameDifficultySelect from "./GameDifficultySelect";
import GameKeyboardShortcutsScreen from "./GameKeyboardShortcutsScreen";
import GameKeyPowerBar from "./GameKeyPowerBar";
import KeyUsageScreen from "./KeyUsageScreen";
import MemoryRecallGame from "./MemoryRecallGame";
import { reportQuestCommonGem, reportQuestGameFinished, reportQuestMemorySession, reportQuestTestNextLevelPlay } from "../utils/questProgressReporter";
import { fetchLastGameWordInsights } from "../utils/questProgressHelpers";
import RewardWheelModal from "./RewardWheelModal";
import GamePotionHint from "./GamePotionHint";
import GamePotionNav from "./GamePotionNav";
import GameProgressDelta, {
  CelebrationSparkles,
  CELEBRATION_STARS_DURATION_MS,
} from "./GameProgressDelta";
import "./GameProgressDelta.css";
import "./GameKeyPowerBar.css";
import "./VocabTooltip.css";
import "./GameQCM.css";
import "./MemoryRecallGame.css";

const publicUrl = process.env.PUBLIC_URL || "";
const FEEDBACK_DELAY_MS = 2000;
const POTION_HINT_DISPLAY_MS = 3000;

// Encadré de bonne réponse : même style et mêmes étoiles que le cadre « Fantastique ».
function CorrectAnswerFeedback({ text }) {
  const [isAnimating, setIsAnimating] = useState(false);
  return (
    <div
      className={`game-qcm-feedback game-qcm-feedback--celebrate${
        isAnimating ? " game-qcm-feedback--animating" : ""
      }`}
      style={{ "--celebration-shimmer-duration": `${CELEBRATION_STARS_DURATION_MS}ms` }}
    >
      <CelebrationSparkles onAnimatingChange={setIsAnimating} />
      <span className="game-qcm-feedback__title">{text}</span>
    </div>
  );
}

/** TEMPORAIRE — passer à true pour prévisualiser la roue des récompenses à la place du jeu */
const PREVIEW_REWARD_WHEEL = false;

/** TEMPORAIRE — passer à true pour prévisualiser l’écran de fin sans jouer */
const PREVIEW_GAME_FINISH_SCREEN = false;
/** TEMPORAIRE — true = écran coffre ; false = écran « Fin de partie » (preview uniquement) */
const PREVIEW_GAME_CHEST_REWARD = true;
/** TEMPORAIRE — trèfle affiché dans le cadre Fantastique (preview uniquement, null = aucun) */
const PREVIEW_CLOVER_POTION_ID = null;
/** TEMPORAIRE — score simulé en preview */
const PREVIEW_GAME_SCORE = 14;
/** TEMPORAIRE — progression simulée en preview */
const PREVIEW_GAME_PROGRESS_DELTA = { discovery: 1, mastery: 6 };

/**
 * Scénarios de test preview (bloc useEffect ~ligne 220) :
 *
 * A) Pas de coffre, aucun point gagné ni perdu :
 *    PREVIEW_GAME_CHEST_REWARD = false
 *    setScore(8)   // < 12 → pas de coffre en jeu réel non plus
 *    progressDelta: { discovery: 0, mastery: 0 }
 *
 * B) Pas de coffre, gains découverte et/ou maîtrise :
 *    PREVIEW_GAME_CHEST_REWARD = false
 *    progressDelta: { discovery: 3, mastery: 5 }  // ajuster les valeurs
 *
 * C) Coffre + perte de maîtrise :
 *    PREVIEW_GAME_CHEST_REWARD = true
 *    setScore(13)
 *    progressDelta: { discovery: 0, mastery: -3 }
 *
 * C2) Perte de maîtrise + gain de découverte :
 *    progressDelta: { discovery: 1, mastery: -3 }
 *
 * D) Coffre + gains + trèfle majeure (15/15) :
 *    PREVIEW_GAME_CHEST_REWARD = true
 *    PREVIEW_CLOVER_POTION_ID = CLOVER_POTION_IDS.MAJEUR
 *    setScore(15)
 *    progressDelta: { discovery: 0, mastery: 8 }
 *
 * E) Trèfle mineure sans coffre (11/15) :
 *    PREVIEW_GAME_CHEST_REWARD = false
 *    PREVIEW_CLOVER_POTION_ID = CLOVER_POTION_IDS.MINEUR
 *    PREVIEW_GAME_SCORE = 11
 *    PREVIEW_GAME_PROGRESS_DELTA = { discovery: 2, mastery: 0 }
 *
 * F) 10/15, perte de maîtrise, pas de coffre ni trèfle :
 *    PREVIEW_GAME_CHEST_REWARD = false
 *    PREVIEW_CLOVER_POTION_ID = null
 *    PREVIEW_GAME_SCORE = 10
 *    PREVIEW_GAME_PROGRESS_DELTA = { discovery: 0, mastery: -1 }
 *
 * G) Coffre sans trèfle (14/15) :
 *    PREVIEW_GAME_CHEST_REWARD = true
 *    PREVIEW_CLOVER_POTION_ID = null
 *    PREVIEW_GAME_SCORE = 14
 *    PREVIEW_GAME_PROGRESS_DELTA = { discovery: 1, mastery: 6 }
 */

function prepareGameSession(json) {
  const questions = Array.isArray(json.questions) ? json.questions : [];
  const discoveryCandidates = Array.isArray(json.discoverySwapCandidates)
    ? json.discoverySwapCandidates
    : [];
  const mixtureCandidates = Array.isArray(json.mixtureSwapCandidates)
    ? json.mixtureSwapCandidates
    : [];
  const bouillonCandidates = Array.isArray(json.bouillonSwapCandidates)
    ? json.bouillonSwapCandidates
    : [];
  const grogCandidates = Array.isArray(json.grogSwapCandidates)
    ? json.grogSwapCandidates
    : [];
  const brucolaqueCandidates = Array.isArray(json.brucolaqueSwapCandidates)
    ? json.brucolaqueSwapCandidates
    : [];
  const neverPlayedInPool = new Set(
    Array.isArray(json.neverPlayedMotsInPool)
      ? json.neverPlayedMotsInPool
      : [
          ...discoveryCandidates.map((q) => q.mot),
          ...questions.filter((q) => q.alreadyPlayed === false).map((q) => q.mot),
        ]
  );

  const enrichedQuestions = questions.map((q) => {
    const gameCount =
      typeof q.gameCount === "number"
        ? q.gameCount
        : q.alreadyPlayed === false
          ? 0
          : q.alreadyPlayed === true
            ? 1
            : neverPlayedInPool.has(q.mot)
              ? 0
              : 1;

    return {
      ...q,
      gameCount,
      alreadyPlayed: gameCount >= 1,
      playedThreePlus:
        typeof q.playedThreePlus === "boolean" ? q.playedThreePlus : gameCount >= 3,
      playedExactlyOnce:
        typeof q.playedExactlyOnce === "boolean"
          ? q.playedExactlyOnce
          : gameCount === 1,
      playedExactlyTwice:
        typeof q.playedExactlyTwice === "boolean"
          ? q.playedExactlyTwice
          : gameCount === 2,
      playedVeryRecently:
        typeof q.playedVeryRecently === "boolean" ? q.playedVeryRecently : false,
    };
  });

  return {
    questions: enrichedQuestions,
    discoveryCandidates,
    bouillonCandidates,
    mixtureCandidates,
    grogCandidates,
    brucolaqueCandidates,
  };
}

function buildRewardGem(serverGem) {
  if (!serverGem?.id) return null;

  const catalog = buildGemCatalog(GEM_FILES);
  const found = catalog.find((gem) => gem.id === serverGem.id);
  if (found) return found;

  const filename = serverGem.filename || `${serverGem.id}.png`;
  const publicUrl = process.env.PUBLIC_URL || "";
  return {
    id: serverGem.id,
    filename,
    label: serverGem.label || serverGem.id,
    imageUrl: `${publicUrl}/gems/${encodeURIComponent(filename)}`,
    count: 0,
  };
}

function renderGradeInLaurel(grade) {
  const match = /^([A-F])([+-])$/.exec(grade);
  if (!match) return grade;

  return (
    <>
      <span className="containerGrade__letter">{match[1]}</span>
      <sup className="containerGrade__modifier">{match[2]}</sup>
    </>
  );
}

function getPlayCountTooltip(gameCount) {
  if (gameCount === 0) {
    return "Mot jamais joué ✶ la gradation EWSS apparaît après 3 parties sur ce mot.";
  }
  if (gameCount === 1) {
    return "Mot joué 1 fois ✶ la gradation EWSS apparaît encore après 2 parties sur ce mot.";
  }
  return "Mot joué 2 fois ✶ la gradation EWSS apparaît après 1 partie supplémentaire sur ce mot.";
}

function GameWordGrade({ grade, insufficientData, gameCount = 0 }) {
  const hasGrade = !insufficientData && grade;
  const showPlayCount = insufficientData && gameCount <= 2;

  return (
    <div className="game-qcm-word-grade">
      <span className="game-qcm-word-grade__row">
        <span className="game-qcm-word-grade__grade-wrap vocab-tooltip-wrap">
          <span
            className={`game-qcm-word-grade__value${
              hasGrade ? "" : " game-qcm-word-grade__value--na"
            }`}
            aria-label={
              hasGrade
                ? `Gradation actuelle : ${grade}`
                : showPlayCount
                  ? `Gradation indisponible ✶ mot joué ${gameCount} fois`
                  : "Gradation indisponible ✶ données insuffisantes"
            }
          >
            {hasGrade ? renderGradeInLaurel(grade) : "—"}
          </span>
          <span className="vocab-tooltip vocab-tooltip--below game-qcm-word-grade__tooltip">
            gradation actuelle du mot à retrouver
          </span>
        </span>
        {showPlayCount && (
          <span className="game-qcm-word-grade__play-count-wrap vocab-tooltip-wrap">
            <span className="game-qcm-word-grade__play-count">{gameCount}</span>
            <span
              className="vocab-tooltip vocab-tooltip--below game-qcm-word-grade__play-count-tooltip"
              role="tooltip"
            >
              {getPlayCountTooltip(gameCount)}
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

function FitDefinition({ text, ewssGrade, ewssInsufficientData, gameCount = 0 }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const el = textRef.current;
    if (!container || !el) return;

    const fit = () => {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--text-scale")
        .trim();
      const textScale = Number(raw);
      const scale = Number.isFinite(textScale) && textScale > 0 ? textScale : 1;
      let size = 14 * scale;
      el.style.fontSize = `${size}px`;

      while (el.scrollHeight > container.clientHeight && size > 8 * scale) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    };

    fit();
    const observer = new MutationObserver(fit);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, [text]);

  return (
    <div className="game-qcm-definition-section">
      <div className="game-qcm-word-grade-wrap">
        <GameWordGrade
          grade={ewssGrade}
          insufficientData={ewssInsufficientData}
          gameCount={gameCount}
        />
      </div>
      <div className="game-qcm-definition-wrap">
        <img
          src={arabesqueGameImg}
          alt=""
          aria-hidden="true"
          className="game-qcm-definition-arabesque"
        />
        <div ref={containerRef} className="game-qcm-definition-fit">
          <div ref={textRef} className="game-qcm-definition">
            {text}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GameQCM({ onContinueToMastery }) {
  const [phase, setPhase] = useState("select");
  const [prepScreen, setPrepScreen] = useState("difficulty");
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  const [mots, setMots] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answer, setAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [testingFixedWords, setTestingFixedWords] = useState(false);

  const revealedRef = useRef(false);
  const inputRef = useRef(null);
  const answersRef = useRef([]);
  const questionStartedAtRef = useRef(null);
  const questionTimeLimitMsRef = useRef(QUESTION_TIME_LIMIT_SECONDS * 1000);
  const questionPotionAidRef = useRef(null);
  const savedRef = useRef(false);
  const [finishData, setFinishData] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [maxUnlockedLevel, setMaxUnlockedLevel] = useState(1);
  const [masteryLoading, setMasteryLoading] = useState(true);

  const [potionCounts, setPotionCounts] = useState({});
  const [usedPotionIds, setUsedPotionIds] = useState([]);
  const [potionUsedThisQuestion, setPotionUsedThisQuestion] = useState(false);
  const [potionHint, setPotionHint] = useState(null);
  const pendingPotionsRef = useRef([]);
  const discoveryCandidatesRef = useRef([]);
  const bouillonCandidatesRef = useRef([]);
  const mixtureCandidatesRef = useRef([]);
  const grogCandidatesRef = useRef([]);
  const brucolaqueCandidatesRef = useRef([]);
  const { registerPotionNav, registerPrepNav } = useGameSessionNav();
  const [timerResetKey, setTimerResetKey] = useState(0);
  const [timerExtendKey, setTimerExtendKey] = useState(0);
  const [discoveryCandidatesCount, setDiscoveryCandidatesCount] = useState(0);
  const [bouillonCandidatesCount, setBouillonCandidatesCount] = useState(0);
  const [mixtureCandidatesCount, setMixtureCandidatesCount] = useState(0);
  const [grogCandidatesCount, setGrogCandidatesCount] = useState(0);
  const [brucolaqueCandidatesCount, setBrucolaqueCandidatesCount] = useState(0);
  const [activeKeyId, setActiveKeyId] = useState(null);
  const [memoryWords, setMemoryWords] = useState([]);
  const [playMode, setPlayMode] = useState(GAME_PLAY_MODES.STANDARD);
  const playModeRef = useRef(GAME_PLAY_MODES.STANDARD);

  const updatePlayMode = (nextMode) => {
    const next =
      nextMode === GAME_PLAY_MODES.TEST
        ? GAME_PLAY_MODES.TEST
        : GAME_PLAY_MODES.STANDARD;
    playModeRef.current = next;
    setPlayMode(next);
  };

  useEffect(() => {
    if (!PREVIEW_GAME_FINISH_SCREEN) return;
    savedRef.current = true;
    setSelectedLevel(1);
    setScore(PREVIEW_GAME_SCORE);
    setMots(new Array(GAME_QUESTION_COUNT).fill(null));
    setFinishData({
      progressDelta: PREVIEW_GAME_PROGRESS_DELTA,
      chest: PREVIEW_GAME_CHEST_REWARD
        ? { earned: true, gem: { id: "améthyste", filename: "améthyste.png" } }
        : { earned: false, gem: null },
      cloverReward: PREVIEW_CLOVER_POTION_ID
        ? { earned: true, potionId: PREVIEW_CLOVER_POTION_ID }
        : { earned: false, potionId: null },
    });
    setPhase("finished");
    setSaveLoading(false);
  }, []);

  const loadMasteryUnlocks = useCallback(async () => {
    setMasteryLoading(true);
    try {
      const res = await fetch(`${API_URL}/mastery/progress`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const unlockedLevel = data.maxUnlockedLevel ?? 1;
        setMaxUnlockedLevel(unlockedLevel);
      }
    } catch (err) {
      console.error("Erreur chargement progression maîtrise :", err);
    } finally {
      setMasteryLoading(false);
    }
  }, []);

  const loadPotionCounts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/potions/counts`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setPotionCounts(data.counts || {});
      }
    } catch (err) {
      console.error("Erreur chargement potions :", err);
    }
  }, []);

  useEffect(() => {
    if (phase === "select") {
      loadMasteryUnlocks();
    }
  }, [phase, loadMasteryUnlocks]);

  const levelInfo = GAME_LEVELS.find((l) => l.id === selectedLevel);
  const motActuel = mots[current];
  const totalQuestions = mots.length;

  const startSession = async (levelId) => {
    setSessionLoading(true);
    setSessionError(null);
    setSelectedLevel(levelId);

    try {
      const isTestPlay = isGameTestPlayMode(playModeRef.current);
      const requestedKeyId = readKeyUsagePreference();
      const canRequestKey =
        !isTestPlay &&
        requestedKeyId &&
        levelId >= MIN_KEY_LEVEL &&
        levelId === maxUnlockedLevel;

      const params = new URLSearchParams({
        level: String(levelId),
        count: String(GAME_QUESTION_COUNT),
      });
      if (canRequestKey) {
        params.set("keyId", requestedKeyId);
      }
      if (isTestPlay) {
        params.set("playMode", GAME_PLAY_MODES.TEST);
      }

      const res = await fetch(`${API_URL}/game/session?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }

      const {
        questions,
        discoveryCandidates,
        bouillonCandidates,
        mixtureCandidates,
        grogCandidates,
        brucolaqueCandidates,
      } = prepareGameSession(json);
      discoveryCandidatesRef.current = discoveryCandidates;
      bouillonCandidatesRef.current = bouillonCandidates;
      mixtureCandidatesRef.current = mixtureCandidates;
      grogCandidatesRef.current = grogCandidates;
      brucolaqueCandidatesRef.current = brucolaqueCandidates;
      setDiscoveryCandidatesCount(discoveryCandidates.length);
      setBouillonCandidatesCount(bouillonCandidates.length);
      setMixtureCandidatesCount(mixtureCandidates.length);
      setGrogCandidatesCount(grogCandidates.length);
      setBrucolaqueCandidatesCount(brucolaqueCandidates.length);
      setActiveKeyId(json.activeKey || null);
      if (json.keyConsumed) {
        writeKeyUsagePreference(null);
      }
      setTimerResetKey(0);
      setTimerExtendKey(0);
      setMots(questions);
      setTestingFixedWords(json.testingFixedWords === true);
      setCurrent(0);
      setScore(0);
      setAnswer("");
      setFeedback("");
      setInputDisabled(false);
      setIsFrozen(false);
      revealedRef.current = false;
      answersRef.current = [];
      savedRef.current = false;
      setFinishData(null);
      setSaveLoading(false);
      setUsedPotionIds([]);
      setPotionUsedThisQuestion(false);
      setPotionHint(null);
      pendingPotionsRef.current = [];
      await loadPotionCounts();
      setPhase("playing");
    } catch (err) {
      console.error("Erreur session jeu :", err);
      setSessionError(err.message || "Impossible de lancer la partie.");
      setSelectedLevel(null);
    } finally {
      setSessionLoading(false);
    }
  };

  const handleUsePotion = useCallback(
    (potionId) => {
      if (activeKeyId) return;
      if (
        isGameTestPlayMode(playModeRef.current) &&
        !GAME_TEST_NAV_POTION_IDS.includes(potionId)
      ) {
        return;
      }
      if (inputDisabled || !motActuel || potionUsedThisQuestion) return;
      if (motActuel.potionsLocked) return;
      if (usedPotionIds.includes(potionId)) return;
      if ((potionCounts[potionId] || 0) < 1) return;

      if (potionId === "decouverte") {
        if (motActuel.alreadyPlayed !== true) return;

        const candidates = discoveryCandidatesRef.current;
        if (!candidates.length) return;

        const swapIndex = Math.floor(Math.random() * candidates.length);
        const replacement = candidates[swapIndex];
        const nextCandidates = candidates.filter((_, index) => index !== swapIndex);
        discoveryCandidatesRef.current = nextCandidates;
        setDiscoveryCandidatesCount(nextCandidates.length);

        setMots((prev) => {
          const next = [...prev];
          next[current] = { ...replacement, alreadyPlayed: false };
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "decouverte" });
        setPotionUsedThisQuestion(true);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        revealedRef.current = false;
        return;
      }

      if (potionId === "bouillon-deja-vu") {
        if (motActuel.playedThreePlus !== true) return;

        const candidates = bouillonCandidatesRef.current;
        if (!candidates.length) return;

        const swapIndex = Math.floor(Math.random() * candidates.length);
        const replacement = candidates[swapIndex];
        const nextCandidates = candidates.filter((_, index) => index !== swapIndex);
        bouillonCandidatesRef.current = nextCandidates;
        setBouillonCandidatesCount(nextCandidates.length);

        setMots((prev) => {
          const next = [...prev];
          next[current] = {
            ...replacement,
            gameCount: 1,
            alreadyPlayed: true,
            playedExactlyOnce: true,
            playedExactlyTwice: false,
            playedThreePlus: false,
          };
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "bouillon-deja-vu" });
        setPotionUsedThisQuestion(true);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        revealedRef.current = false;
        return;
      }

      if (potionId === "mixture-3eme-oeil") {
        if (motActuel.playedThreePlus !== true) return;

        const candidates = mixtureCandidatesRef.current;
        if (!candidates.length) return;

        const swapIndex = Math.floor(Math.random() * candidates.length);
        const replacement = candidates[swapIndex];
        const nextCandidates = candidates.filter((_, index) => index !== swapIndex);
        mixtureCandidatesRef.current = nextCandidates;
        setMixtureCandidatesCount(nextCandidates.length);

        setMots((prev) => {
          const next = [...prev];
          next[current] = {
            ...replacement,
            alreadyPlayed: true,
            playedExactlyTwice: true,
            playedThreePlus: false,
          };
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "mixture-3eme-oeil" });
        setPotionUsedThisQuestion(true);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        revealedRef.current = false;
        return;
      }

      if (potionId === "grog-veuve-noire") {
        if (motActuel.isMastered !== true) return;

        const candidates = grogCandidatesRef.current;
        if (!candidates.length) return;

        const swapIndex = Math.floor(Math.random() * candidates.length);
        const replacement = candidates[swapIndex];
        const nextCandidates = candidates.filter((_, index) => index !== swapIndex);
        grogCandidatesRef.current = nextCandidates;
        setGrogCandidatesCount(nextCandidates.length);

        setMots((prev) => {
          const next = [...prev];
          next[current] = { ...replacement };
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "grog-veuve-noire" });
        setPotionUsedThisQuestion(true);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        revealedRef.current = false;
        return;
      }

      if (potionId === "mixture-du-brucolaque") {
        if (motActuel.playedVeryRecently !== true) return;

        const candidates = brucolaqueCandidatesRef.current;
        if (!candidates.length) return;

        const swapIndex = Math.floor(Math.random() * candidates.length);
        const replacement = candidates[swapIndex];
        const nextCandidates = candidates.filter((_, index) => index !== swapIndex);
        brucolaqueCandidatesRef.current = nextCandidates;
        setBrucolaqueCandidatesCount(nextCandidates.length);

        setMots((prev) => {
          const next = [...prev];
          next[current] = { ...replacement, playedVeryRecently: false };
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "mixture-du-brucolaque" });
        setPotionUsedThisQuestion(true);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        revealedRef.current = false;
        return;
      }

      if (potionId === "baume-sursis") {
        if (current >= mots.length - 1) return;

        setMots((prev) => {
          const next = [...prev];
          const deferred = { ...next[current], potionsLocked: true };
          next.splice(current, 1);
          next.push(deferred);
          return next;
        });
        setAnswer("");
        setPotionHint({ type: "baume-sursis" });
        setPotionUsedThisQuestion(false);
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerResetKey((key) => key + 1);
        setTimerExtendKey(0);
        revealedRef.current = false;
        return;
      }

      if (potionId === "serum-cadran") {
        setPotionHint({ type: "serum-cadran" });
        setPotionUsedThisQuestion(true);
        questionPotionAidRef.current = "T";
        questionTimeLimitMsRef.current = QUESTION_TIME_EXTENDED_LIMIT_SECONDS * 1000;
        const nextUsed = [...usedPotionIds, potionId];
        setUsedPotionIds(nextUsed);
        pendingPotionsRef.current = nextUsed;
        setTimerExtendKey((key) => key + 1);
        return;
      }

      const hint = buildPotionHint(potionId, motActuel.mot);
      if (!hint) return;

      const potionAid = getGameAnswerPotionAid(potionId);
      if (potionAid) {
        questionPotionAidRef.current = potionAid;
      }

      const nextUsed = [...usedPotionIds, potionId];
      setPotionHint(hint);
      setPotionUsedThisQuestion(true);
      setUsedPotionIds(nextUsed);
      pendingPotionsRef.current = nextUsed;
    },
    [
      activeKeyId,
      current,
      inputDisabled,
      motActuel,
      mots.length,
      potionCounts,
      potionUsedThisQuestion,
      usedPotionIds,
    ]
  );

  const potionsDisabledByKey = Boolean(activeKeyId);
  const potionsLocked = potionsDisabledByKey || motActuel?.potionsLocked === true;
  const discoveryCanUse =
    !potionsLocked && motActuel?.alreadyPlayed === true && discoveryCandidatesCount > 0;
  const discoveryUnavailableReason = !motActuel
    ? ""
    : motActuel.alreadyPlayed !== true
      ? "never-played"
      : potionUsedThisQuestion
        ? "potion-used"
        : inputDisabled
          ? "waiting"
          : discoveryCandidatesCount === 0
            ? "no-candidates"
            : "";

  const mixtureCanUse =
    !potionsLocked && motActuel?.playedThreePlus === true && mixtureCandidatesCount > 0;
  const mixtureUnavailableReason = !motActuel
    ? ""
    : motActuel.playedThreePlus !== true
      ? "not-three-plus"
      : potionUsedThisQuestion
        ? "potion-used"
        : inputDisabled
          ? "waiting"
          : mixtureCandidatesCount === 0
            ? "no-candidates"
            : "";

  const bouillonCanUse =
    !potionsLocked && motActuel?.playedThreePlus === true && bouillonCandidatesCount > 0;
  const bouillonUnavailableReason = !motActuel
    ? ""
    : motActuel.playedThreePlus !== true
      ? "not-three-plus"
      : potionUsedThisQuestion
        ? "potion-used"
        : inputDisabled
          ? "waiting"
          : bouillonCandidatesCount === 0
            ? "no-candidates"
            : "";

  const grogCanUse =
    !potionsLocked && motActuel?.isMastered === true && grogCandidatesCount > 0;
  const grogUnavailableReason = !motActuel
    ? ""
    : potionsLocked
      ? "deferred-question"
      : motActuel.isMastered !== true
      ? "not-mastered"
      : potionUsedThisQuestion
        ? "potion-used"
        : inputDisabled
          ? "waiting"
          : grogCandidatesCount === 0
            ? "no-candidates"
            : "";

  const brucolaqueCanUse =
    !potionsLocked && motActuel?.playedVeryRecently === true && brucolaqueCandidatesCount > 0;
  const brucolaqueUnavailableReason = !motActuel
    ? ""
    : potionsLocked
      ? "deferred-question"
      : motActuel.playedVeryRecently !== true
        ? "not-very-recent"
        : potionUsedThisQuestion
          ? "potion-used"
          : inputDisabled
            ? "waiting"
            : brucolaqueCandidatesCount === 0
              ? "no-candidates"
              : "";

  const sursisCanUse =
    !potionsLocked &&
    current < totalQuestions - 1 &&
    !potionUsedThisQuestion &&
    !inputDisabled;
  const sursisUnavailableReason = !motActuel
    ? ""
    : potionsLocked
      ? "deferred-question"
      : current >= totalQuestions - 1
        ? "last-question"
        : potionUsedThisQuestion
          ? "potion-used"
          : inputDisabled
            ? "waiting"
            : "";

  useEffect(() => {
    registerPrepNav(
      {
        visible: phase === "select",
        screen: prepScreen,
      },
      setPrepScreen
    );

    return () => {
      registerPrepNav({ visible: false, screen: "difficulty" }, null);
    };
  }, [phase, prepScreen, registerPrepNav]);

  useEffect(() => {
    if (phase !== "playing" || activeKeyId) return undefined;

    const handleShortcutKeyDown = (event) => {
      const potionId = getPotionIdForShortcutEvent(event);
      if (!potionId) return;
      if (
        isGameTestPlayMode(playModeRef.current) &&
        !GAME_TEST_NAV_POTION_IDS.includes(potionId)
      ) {
        return;
      }

      // Capture : fonctionne même quand le champ de réponse a le focus.
      event.preventDefault();
      event.stopPropagation();
      handleUsePotion(potionId);
    };

    window.addEventListener("keydown", handleShortcutKeyDown, true);
    return () => window.removeEventListener("keydown", handleShortcutKeyDown, true);
  }, [phase, activeKeyId, handleUsePotion]);

  useEffect(() => {
    registerPotionNav(
      {
        visible: phase === "playing" && Boolean(motActuel) && !activeKeyId,
        playMode,
        potionCounts,
        usedPotionIds,
        potionUsedThisQuestion,
        inputDisabled,
        potionsLocked,
        discoveryCanUse,
        discoveryUnavailableReason,
        bouillonCanUse,
        bouillonUnavailableReason,
        mixtureCanUse,
        mixtureUnavailableReason,
        grogCanUse,
        grogUnavailableReason,
        brucolaqueCanUse,
        brucolaqueUnavailableReason,
        sursisCanUse,
        sursisUnavailableReason,
      },
      handleUsePotion
    );
  }, [
    phase,
    playMode,
    activeKeyId,
    motActuel,
    potionCounts,
    usedPotionIds,
    potionUsedThisQuestion,
    inputDisabled,
    potionsLocked,
    discoveryCanUse,
    discoveryUnavailableReason,
    bouillonCanUse,
    bouillonUnavailableReason,
    mixtureCanUse,
    mixtureUnavailableReason,
    grogCanUse,
    grogUnavailableReason,
    brucolaqueCanUse,
    brucolaqueUnavailableReason,
    sursisCanUse,
    sursisUnavailableReason,
    discoveryCandidatesCount,
    bouillonCandidatesCount,
    mixtureCandidatesCount,
    grogCandidatesCount,
    brucolaqueCandidatesCount,
    registerPotionNav,
    handleUsePotion,
  ]);

  const resetToSelect = () => {
    setPhase("select");
    setPrepScreen("difficulty");
    setSelectedLevel(null);
    setMots([]);
    setCurrent(0);
    setScore(0);
    setAnswer("");
    setFeedback("");
    setSessionError(null);
    setTestingFixedWords(false);
    setFinishData(null);
    setSaveLoading(false);
    savedRef.current = false;
    setUsedPotionIds([]);
    setPotionUsedThisQuestion(false);
    setPotionHint(null);
    pendingPotionsRef.current = [];
    discoveryCandidatesRef.current = [];
    bouillonCandidatesRef.current = [];
    mixtureCandidatesRef.current = [];
    grogCandidatesRef.current = [];
    brucolaqueCandidatesRef.current = [];
    setDiscoveryCandidatesCount(0);
    setBouillonCandidatesCount(0);
    setMixtureCandidatesCount(0);
    setGrogCandidatesCount(0);
    setBrucolaqueCandidatesCount(0);
    setActiveKeyId(null);
    setMemoryWords([]);
    setTimerResetKey(0);
    setTimerExtendKey(0);
    updatePlayMode(GAME_PLAY_MODES.STANDARD);
  };

  const handleContinueToMastery = () => {
    resetToSelect();
    onContinueToMastery?.();
  };

  const saveMemoryGameResult = async (result) => {
    if (!selectedLevel || !result) return;

    try {
      await fetch(`${API_URL}/game/memory-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          level: selectedLevel,
          score: result.score,
          totalQuestions: result.totalQuestions,
          responseTimeMs: result.responseTimeMs,
        }),
      });
    } catch (err) {
      console.error("Erreur enregistrement jeu de mémoire :", err);
    }
  };

  const handleMemoryGameComplete = async (result) => {
    if (!isGameTestPlayMode(playModeRef.current)) {
      await saveMemoryGameResult(result);
      reportQuestMemorySession({
        score: result.score,
        totalQuestions: result.totalQuestions,
      });
    }
    handleContinueToMastery();
  };

  const getPlayedWords = () =>
    mots.map((entry) => entry?.mot).filter((word) => Boolean(word));

  const launchMemoryGame = (words) => {
    setMemoryWords(words);
    setPhase("memory");
  };

  const handleFinishFlowContinue = () => {
    const playedWords = getPlayedWords();
    if (shouldProposeMemoryGame() && playedWords.length > 0) {
      launchMemoryGame(playedWords);
      return;
    }

    handleContinueToMastery();
  };

  const finishFlowContinueLabel =
    shouldProposeMemoryGame() && getPlayedWords().length > 0
      ? "Tester ma mémoire"
      : "Continuer";

  const goToNextQuestion = () => {
    setFeedback("");
    setAnswer("");
    setInputDisabled(false);
    setIsFrozen(false);
    setPotionUsedThisQuestion(false);
    setPotionHint(null);
    revealedRef.current = false;
    setTimerExtendKey(0);

    if (current + 1 < mots.length) {
      setCurrent((c) => c + 1);
    } else {
      setPhase("finished");
    }
  };

  const revealAnswer = (isCorrect, givenAnswer) => {
    if (revealedRef.current || !motActuel) return;
    revealedRef.current = true;

    const given = (givenAnswer ?? answer ?? "").trim();
    const startedAt = questionStartedAtRef.current;
    const rawResponseMs =
      startedAt != null ? Math.max(0, Date.now() - startedAt) : null;
    const responseMs =
      rawResponseMs != null
        ? Math.min(rawResponseMs, questionTimeLimitMsRef.current)
        : null;

    answersRef.current.push({
      mot: motActuel.mot,
      given,
      correct: isCorrect,
      responseMs,
      potionAid: questionPotionAidRef.current,
    });

    setInputDisabled(true);
    setIsFrozen(true);
    setAnswer(motActuel.mot);

    setFeedbackCorrect(isCorrect);
    if (isCorrect) {
      setScore((s) => s + 1);
      setFeedback(`✨ BONNE RÉPONSE ! ✨`);
    } else {
      setFeedback(`❌ Dommage, le mot à découvrir était : ${motActuel.mot}`);
    }

    setTimeout(goToNextQuestion, FEEDBACK_DELAY_MS);
  };

  const handleTimesUp = () => {
    if (inputDisabled || !motActuel) return;
    const trimmed = (answer || "").trim();
    const isCorrect =
      trimmed.length > 0 && typedAnswersMatch(motActuel.mot, trimmed);
    revealAnswer(isCorrect, trimmed);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputDisabled || !motActuel) return;
    const trimmed = answer.trim();
    const isCorrect = typedAnswersMatch(motActuel.mot, trimmed);
    revealAnswer(isCorrect, trimmed);
  };

  const handleQuestionTimerStart = useCallback(() => {
    questionStartedAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (phase !== "playing" || !motActuel) return;
    questionPotionAidRef.current = null;
    questionTimeLimitMsRef.current =
      activeKeyId === "zamselly"
        ? QUESTION_TIME_EXTENDED_LIMIT_SECONDS * 1000
        : QUESTION_TIME_LIMIT_SECONDS * 1000;
  }, [phase, current, timerResetKey, motActuel?.mot, activeKeyId]);

  useEffect(() => {
    if (!potionHint || !shouldAutoHidePotionHint(potionHint)) return;
    const timerId = window.setTimeout(() => {
      setPotionHint(null);
    }, POTION_HINT_DISPLAY_MS);
    return () => window.clearTimeout(timerId);
  }, [potionHint]);

  useEffect(() => {
    if (phase !== "playing" || inputDisabled) return;
    const frameId = window.requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [current, inputDisabled, phase, potionHint, timerResetKey, timerExtendKey]);

  useEffect(() => {
    if (phase !== "playing" || inputDisabled || !motActuel) return;
    if (!testingFixedWords) return;
    setAnswer(motActuel.mot);
  }, [current, motActuel, phase, inputDisabled, testingFixedWords]);

  useEffect(() => {
    if (PREVIEW_GAME_FINISH_SCREEN) return;
    if (phase !== "finished" || savedRef.current || !selectedLevel) return;
    savedRef.current = true;
    setSaveLoading(true);

    const isTestPlay = isGameTestPlayMode(playModeRef.current);

    fetch(`${API_URL}/game/result`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        level: selectedLevel,
        score,
        totalQuestions,
        answers: isTestPlay ? [] : answersRef.current,
        potionsUsed: pendingPotionsRef.current,
        activeKey: isTestPlay ? null : activeKeyId,
        ...(isTestPlay ? { playMode: GAME_PLAY_MODES.TEST } : {}),
      }),
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json.message || `HTTP ${res.status}`);
        }
        setFinishData(json);
        if (json.potionCounts) {
          setPotionCounts(json.potionCounts);
        }
        if (!isTestPlay) {
          dispatchNotificationsChanged();
        }
        setSaveLoading(false);

        if (isTestPlay) {
          try {
            await reportQuestTestNextLevelPlay({
              level: selectedLevel,
              maxUnlockedLevel,
            });
          } catch (questErr) {
            console.warn("Quêtes — partie mode T :", questErr.message);
          }
          return;
        }

        try {
          const wordInsights = Array.isArray(json.wordInsights)
            ? json.wordInsights
            : await fetchLastGameWordInsights();
          const unlockedAfter = json.mastery?.maxUnlockedLevel ?? maxUnlockedLevel;
          await reportQuestGameFinished({
            score,
            totalQuestions,
            level: selectedLevel,
            maxUnlockedLevel: unlockedAfter,
            activeKey: activeKeyId,
            potionsUsed: pendingPotionsRef.current,
            progressDelta: json.progressDelta || {},
            wordInsights,
            capturedWordCount: json.mastery?.capturedWordCount,
            masteredWordCount: json.mastery?.masteredWordCount,
          });
        } catch (questErr) {
          console.warn("Quêtes — synchronisation partie :", questErr);
        }

        const chestGemId = json.chest?.gem?.id;
        if (json.chest?.earned && chestGemId && !isPreciousGem(chestGemId)) {
          reportQuestCommonGem({ gemId: chestGemId, source: "chest" });
        }
      })
      .catch((err) => {
        console.error("Erreur enregistrement partie :", err);
        savedRef.current = false;
        setFinishData({
          chest: { earned: false, gem: null },
          cloverReward: { earned: false, potionId: null },
        });
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }, [phase, score, selectedLevel, totalQuestions, activeKeyId, maxUnlockedLevel]);

  const cloverPotionId =
    finishData?.cloverReward?.earned && finishData.cloverReward.potionId
      ? finishData.cloverReward.potionId
      : null;

  if (phase === "select") {
    return (
      <div className="game-qcm game-qcm--select">
        {prepScreen === "key" ? (
          <KeyUsageScreen />
        ) : prepScreen === "shortcuts" ? (
          <GameKeyboardShortcutsScreen />
        ) : (
          <GameDifficultySelect
            onSelect={startSession}
            loading={sessionLoading || masteryLoading}
            error={sessionError}
            maxUnlockedLevel={maxUnlockedLevel}
            playMode={playMode}
            onPlayModeChange={updatePlayMode}
          />
        )}
        {PREVIEW_REWARD_WHEEL && <RewardWheelModal defaultOpen />}
      </div>
    );
  }

  if (phase === "memory") {
    return (
      <div className="game-qcm game-qcm--memory">
        <MemoryRecallGame
          words={memoryWords}
          onComplete={handleMemoryGameComplete}
        />
      </div>
    );
  }

  if (phase === "finished") {
    const isTestPlay = isGameTestPlayMode(playMode);

    if (saveLoading) {
      return (
        <div className="game-qcm">
          <p className="game-qcm-saving">
            {isTestPlay ? "Fin de la partie…" : "Enregistrement de la partie…"}
          </p>
        </div>
      );
    }

    if (!isTestPlay && finishData?.chest?.earned && finishData.chest.gem) {
      const rewardGem = buildRewardGem(finishData.chest.gem);
      if (rewardGem) {
        return (
          <ChestOpenAnimation
            score={score}
            levelId={selectedLevel}
            rewardGem={rewardGem}
            progressDelta={finishData.progressDelta}
            cloverPotionId={cloverPotionId}
            onContinue={handleFinishFlowContinue}
            continueLabel={finishFlowContinueLabel}
            previewAutoOpen={PREVIEW_GAME_FINISH_SCREEN && PREVIEW_GAME_CHEST_REWARD}
          />
        );
      }
    }

    const progressDelta = finishData?.progressDelta;

    return (
      <div className="game-qcm game-qcm--finished">
        <h2 className="game-qcm-title">Fin de la partie</h2>
        {levelInfo && <p className="game-qcm-level-recap">{levelInfo.name}</p>}
        <p className="game-qcm-score-final">
          Score : <strong>{score}</strong> / {totalQuestions}
        </p>
        {!isTestPlay && progressDelta ? (
          <GameProgressDelta
            discovery={progressDelta.discovery}
            mastery={progressDelta.mastery}
            score={score}
            totalQuestions={GAME_QUESTION_COUNT}
            layout="celebration"
            cloverPotionId={cloverPotionId}
          />
        ) : null}
        <button type="button" className="game-qcm-btn game-qcm-btn--continue" onClick={handleFinishFlowContinue}>
          {finishFlowContinueLabel}
        </button>
      </div>
    );
  }

  if (!motActuel) {
    return <div className="game-qcm-error">Aucun mot disponible.</div>;
  }

  const maskedDefinition = maskDefinition(
    motActuel.definition,
    motActuel.motsCaches
  );
  const definitionToShow = feedback ? motActuel.definition : maskedDefinition;

  return (
    <div className="game-qcm game-qcm--playing">
      <div className="game-qcm-header">
        <div className="game-qcm-header__main">
          <h2 className="game-qcm-title">
            Retrouve le mot à partir de sa définition
          </h2>
          {testingFixedWords && (
            <p className="game-qcm-test-banner">
              Mode test : les 15 mots sont identiques à chaque partie.
            </p>
          )}
          <p className="game-qcm-progress">
            Question {current + 1} / {totalQuestions} — Score : {score}
          </p>
          <div
            className="game-qcm-progress-bar"
            role="progressbar"
            aria-valuenow={current + 1}
            aria-valuemin={1}
            aria-valuemax={totalQuestions}
            aria-label={`Avancement : question ${current + 1} sur ${totalQuestions}`}
          >
            <div
              className="game-qcm-progress-bar__fill"
              style={{ width: `${((current + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {levelInfo?.icon && (
          <div className="game-qcm-header__icon-slot">
            <div className="game-qcm-header__icon-wrap">
              <div className="game-qcm-level-lottie-wrap" aria-hidden="true">
                <Lottie
                  animationData={starsAndLightAnimationData}
                  loop
                  autoplay
                  className="game-qcm-level-lottie"
                  rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
                />
              </div>
              <img
                src={`${publicUrl}/icons/${levelInfo.icon}`}
                alt={`Niveau ${selectedLevel}`}
                className="game-qcm-level-icon"
                draggable={false}
              />
            </div>
          </div>
        )}
      </div>

      <CountDownTimer
        key={`${current}-${timerResetKey}`}
        timeLimit={
          activeKeyId === "zamselly"
            ? QUESTION_TIME_EXTENDED_LIMIT_SECONDS
            : QUESTION_TIME_LIMIT_SECONDS
        }
        extendBy={timerExtendKey}
        extendAmount={SERUM_CADRAN_TIME_BONUS_SECONDS}
        extendMaxLimit={QUESTION_TIME_EXTENDED_LIMIT_SECONDS}
        onStart={handleQuestionTimerStart}
        onTimesUp={handleTimesUp}
        isFrozen={isFrozen}
      />

      <FitDefinition
        text={definitionToShow}
        ewssGrade={motActuel.ewssGrade}
        ewssInsufficientData={motActuel.ewssInsufficientData}
        gameCount={motActuel.gameCount ?? 0}
      />

      <div className="game-qcm-footer">
        <div className="game-qcm-answer-row">
          {!feedback ? (
            <form onSubmit={handleSubmit} className="game-qcm-form">
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(filterTypedWord(e.target.value))}
                placeholder="Tape ta réponse…"
                className="game-qcm-input"
                maxLength={WORD_MOT_MAX_LENGTH}
                disabled={inputDisabled}
                autoFocus
              />
              <button
                type="submit"
                className="game-qcm-btn"
                disabled={inputDisabled}
              >
                Valider
              </button>
            </form>
          ) : feedbackCorrect ? (
            <CorrectAnswerFeedback text={feedback} />
          ) : (
            <div className="game-qcm-feedback">{feedback}</div>
          )}
        </div>
      </div>

      <div
        className="game-qcm-potion-nav-slot"
        aria-label={activeKeyId ? "Pouvoir de parchemin" : "Potions"}
      >
        {!activeKeyId && (
          <div className="game-qcm-potion-hint-slot">
            <GamePotionHint hint={potionHint} />
          </div>
        )}
        {activeKeyId ? <GameKeyPowerBar activeKeyId={activeKeyId} /> : <GamePotionNav />}
      </div>
    </div>
  );
}
